import { HttpErrorResponse } from '@angular/common/http';
import { GetNotesTypesNamesAndIdsDTO, INotesTypeEntityDTO } from '@modeso/types__aumico-lib-notes-be';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, createSelector, on } from '@ngrx/store';
import Debug from 'debug';
import * as fromNotesTypesActions from '../actions/notesTypes.actions';
import { INameExistenceFiltration } from '../interfaces/INameExistenceFiltration.interface';

export const notesTypesFeatureKey = 'aumicoNotesTypesMicroservice';

const debug = Debug('aumico:aumico-lib-notes-fe:NotesTypesReducer');

// State Declarations - START
export interface NotesTypesState extends EntityState<INotesTypeEntityDTO> {
    activeNotesTypes: string | null;
    notesTypesNamesAndIds: GetNotesTypesNamesAndIdsDTO[];
    notesTypeError: HttpErrorResponse;
}

export function selectNotesTypesId(a: INotesTypeEntityDTO): string {
    return a._id;
}

export const adapter: EntityAdapter<INotesTypeEntityDTO> = createEntityAdapter<INotesTypeEntityDTO>({
    selectId: selectNotesTypesId,
});

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const selectNotesTypesIds = selectIds;

// select the dictionary of notes types entities
export const selectNotesTypesEntities = selectEntities;

// select the array of notes types
export const selectAllNotesTypes = selectAll;

export const getAllNotesTypesNamesAndIds = (state: NotesTypesState) => state.notesTypesNamesAndIds;

export const SelectAllNotesTypesNamesAndIds = createSelector(getAllNotesTypesNamesAndIds, (notesTypesNamesAndIds) => {
    return notesTypesNamesAndIds;
});

// select the total notes types count
export const selectNotesTypesTotal = selectTotal;

export const selectEntity = createSelector(selectEntities, (entities, props) => entities[props.id]);

export const getActiveNotesTypeId = (state: NotesTypesState) => state.activeNotesTypes;

export const getNotesTypeError = (state: NotesTypesState) => state.notesTypeError;

export const getActiveNotesType = createSelector(selectEntities, getActiveNotesTypeId, (entities, id) => entities[id]);

export const nameExistenceSelector = createSelector(selectEntities, (entities, props: INameExistenceFiltration) => {
    const keys = Object.keys(entities);

    for (const key of keys) {
        const notesType: INotesTypeEntityDTO = entities[key];

        if (notesType.name === props.name && notesType._id !== props.id && notesType.owner === props.owner) {
            return true;
        }
    }

    return false;
});

export const initialNotesTypesState: NotesTypesState = adapter.getInitialState({
    // additional entity state properties
    activeNotesTypes: null,
    notesTypeError: undefined,
    notesTypesNamesAndIds: [],
});

const notesTypesReducer = createReducer(
    initialNotesTypesState,

    on(fromNotesTypesActions.getNotesTypes, (state) => ({
        ...state,
        activeNotesTypes: null,
        notesTypeError: undefined,
    })),
    on(fromNotesTypesActions.onGetNotesTypesSuccessfully, (state, action) => {
        return adapter.setAll(action.payload, { ...state, activeNotesTypes: null, notesTypeError: undefined });
    }),
    on(fromNotesTypesActions.onGetNotesTypesFailed, (state, action) => ({
        ...state,
        activeNotesTypes: null,
        notesTypeError: action.payload,
    })),

    on(fromNotesTypesActions.createNotesType, (state) => ({
        ...state,
        activeNotesTypes: null,
        notesTypeError: undefined,
    })),
    on(fromNotesTypesActions.onCreateNotesTypeSuccessfully, (state, action) => {
        return adapter.addOne(action.payload, { ...state, activeNotesTypes: null, notesTypeError: undefined });
    }),
    on(fromNotesTypesActions.onCreateNotesTypeFailed, (state, action) => ({
        ...state,
        activeNotesTypes: null,
        notesTypeError: action.payload,
    })),

    on(fromNotesTypesActions.updateNotesType, (state) => ({ ...state, notesTypeError: undefined })),
    on(fromNotesTypesActions.onUpdateNotesTypeSuccessfully, (state, action) => {
        return adapter.updateOne(
            { id: action.payload.noteType._id, changes: action.payload.noteType },
            { ...state, activeNotesTypes: null, notesTypeError: undefined },
        );
    }),
    on(fromNotesTypesActions.onUpdateNotesTypeFailed, (state, action) => ({
        ...state,
        notesTypeError: action.payload,
    })),

    on(fromNotesTypesActions.onSelectNotesType, (state, action) => ({
        ...state,
        activeNotesTypes: action.payload,
        notesTypeError: undefined,
    })),

    on(fromNotesTypesActions.getAllNotesTypesNamesAndIds.getAllNotesTypesNamesAndIds, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromNotesTypesActions.getAllNotesTypesNamesAndIds.getAllNotesTypesNamesAndIdsSuccess, (state, action) => ({
        ...state,
        notesTypesNamesAndIds: action.payload,
        error: undefined,
    })),
    on(fromNotesTypesActions.getAllNotesTypesNamesAndIds.getAllNotesTypesNamesAndIdsFailed, (state, action) => ({
        ...state,
        notesTypesNamesAndIds: null,
        error: action.payload,
    })),
);

export function reducer(state: NotesTypesState | undefined, action: Action) {
    return notesTypesReducer(state, action);
}
